import { Injectable } from "@angular/core";
import { EMPTY, Observable, of, switchMap } from "rxjs";
import { ExternalAppUserOpenAiSdk } from "../sdks/external-app-user-open-ai.sdk";
import { ExternalAppUserClioSdk } from "../sdks/external-app-user-clio.sdk";
import { NotificationManager } from "./notification.manager";
import { OpenAiSetting } from "@models/open-ai-setting.model";
import { ExternalAppAccountOpenAiSdk } from "@sdks/external-app-account-open-ai.sdk";
@Injectable({
	providedIn: 'root'
})
export class ThirdPartyManager {
	constructor(
		private clioSdk: ExternalAppUserClioSdk,
		private openAiSdk: ExternalAppUserOpenAiSdk,
		private openAiAccountSdk: ExternalAppAccountOpenAiSdk,
		private notificationManager: NotificationManager
	) { }

	//#region Clio

	public isClioLinked(): Observable<boolean | null> {
		return this.clioSdk.isLinked().pipe(switchMap((data) => {
			if (typeof (data) === 'boolean') {
				return of(data);
			}
			else {
				this.displayErrorNotification('linking to Clio');
				return EMPTY;
			}
		}));
	}

	public unlinkClio(): Observable<boolean | null> {
		return this.clioSdk.delete().pipe(switchMap((data) => {
			if (data) {
				this.displaySuccessNotification('Clio has been unlinked');
				return of(data);
			}
			else {
				this.displayErrorNotification('unlinking from Clio');
				return EMPTY;
			}
		}));
	}

	//#endregion Clio

	//#region OpenAI (ByUserId methods)

	public isOpenAiLinked(): Observable<boolean | null> {
		return this.openAiSdk.isLinked().pipe(switchMap((data) => {
			if (typeof (data) === 'boolean') {
				return of(data);
			}
			this.displayErrorNotification('getting OpenAI settings');
			return EMPTY;
		}));
	}

	public isOpenAiLinkedByUserId(userId: string): Observable<boolean | null> {
		return this.openAiSdk.isLinkedByUserId(userId).pipe(switchMap((data) => {
			if (typeof (data) === 'boolean') {
				return of(data);
			}
			this.displayErrorNotification('getting OpenAI settings');
			return EMPTY;
		}));
	}

	public getOpenAiIntegrationByUserId(userId: string): Observable<OpenAiSetting | null> {
		return this.openAiSdk.getByUserId(userId).pipe(switchMap((data) => {
			if (data) {
				return of(data);
			}
			return EMPTY;
		}));
	}

	public linkOpenAiIntegrationByUserId(userId: string, setting: OpenAiSetting): Observable<OpenAiSetting | null> {
		return this.openAiSdk.postByUserId(userId, setting).pipe(switchMap((data) => {
			if (data) {
				this.displaySuccessNotification('OpenAI has been linked');
				return of(data);
			}
			else {
				this.displayErrorNotification('linking to OpenAI');
				return EMPTY;
			}
		}));
	}

	public updateOpenAiIntegrationByUserId(userId: string, setting: OpenAiSetting): Observable<OpenAiSetting | null> {
		return this.openAiSdk.putByUserId(userId, setting).pipe(switchMap((data) => {
			if (data) {
				this.displaySuccessNotification('OpenAI has been updated');
				return of(data);
			}
			else {
				this.displayErrorNotification('updating OpenAI');
				return EMPTY;
			}
		}));
	}

	public unlinkOpenAiIntegrationByUserId(userId: string): Observable<boolean | null> {
		return this.openAiSdk.deleteByUserId(userId).pipe(switchMap((data) => {
			if (data) {
				this.displaySuccessNotification('OpenAI has been unlinked');
				return of(data);
			}
			else {
				this.displayErrorNotification('unlinking OpenAI');
				return EMPTY;
			}
		}));
	}

	//#endregion OpenAI (ByUserId methods)

	//#region OpenAI (ByAccountId methods)

	public isOpenAiLinkedByAccountId(accountId: string): Observable<boolean | null> {
		return this.openAiAccountSdk.isLinkedByAccountId(accountId).pipe(switchMap((data) => {
			if (typeof (data) === 'boolean') {
				return of(data);
			}
			this.displayErrorNotification('getting OpenAI settings by account');
			return EMPTY;
		}));
	}

	public getOpenAiIntegrationByAccountId(accountId: string): Observable<OpenAiSetting | null> {
		return this.openAiAccountSdk.getByAccountId(accountId).pipe(switchMap((data) => {
			if (data) {
				return of(data);
			}
			return EMPTY;
		}));
	}

	public linkOpenAiIntegrationByAccountId(accountId: string, setting: OpenAiSetting): Observable<OpenAiSetting | null> {
		return this.openAiAccountSdk.postByAccountId(accountId, setting).pipe(switchMap((data) => {
			if (data) {
				this.displaySuccessNotification('OpenAI has been linked (account)');
				return of(data);
			}
			else {
				this.displayErrorNotification('linking to OpenAI by account');
				return EMPTY;
			}
		}));
	}

	public updateOpenAiIntegrationByAccountId(accountId: string, setting: OpenAiSetting): Observable<OpenAiSetting | null> {
		return this.openAiAccountSdk.putByAccountId(accountId, setting).pipe(switchMap((data) => {
			if (data) {
				this.displaySuccessNotification('OpenAI has been updated (account)');
				return of(data);
			}
			else {
				this.displayErrorNotification('updating OpenAI by account');
				return EMPTY;
			}
		}));
	}

	public unlinkOpenAiIntegrationByAccountId(accountId: string): Observable<boolean | null> {
		return this.openAiAccountSdk.deleteByAccountId(accountId).pipe(switchMap((data) => {
			if (data) {
				this.displaySuccessNotification('OpenAI has been unlinked (account)');
				return of(data);
			}
			else {
				this.displayErrorNotification('unlinking OpenAI by account');
				return EMPTY;
			}
		}));
	}

	//#endregion OpenAI (ByAccountId methods)

	private displaySuccessNotification(message: string) {
		this.notificationManager.displaySnackBarMessage({
			message: `${message} successfully.`
		});
	}

	private displayErrorNotification(message: string) {
		this.notificationManager.displaySnackBarMessage({
			message: `An error has occurred when ${message}. Please try again or contact Professional Service for support.`,
			type: 'error',
			ttl: 5000
		});
	}
}